import { AddOrUpdateBauEntryRequest, GetResiduePayoutPotentialRequest, axios } from "utils";

import { PUBLIC_MODULES_URL as endpoint } from "consts";

import { FarmCropsByYearResponse, FullFieldModule, Module, PracticeCrop, WithInjectedName, YearlyDataEntryAddedStatus } from "models";

// this file and endpoints should be refactored!
// every endpoint should have a prefix such as farm/field/shape/etc
// move base endpoint to constants

// modules

export const getModules = async (): Promise<Module[]> => axios.get(`${endpoint}/`);

export const getModule = async (data: { moduleId: string; signal: AbortSignal }) => axios.get(`${endpoint}/${data.moduleId}`, { data });

export const getModuleBySlug = async (data: { moduleSlug: string; signal: AbortSignal }) => axios.get(`${endpoint}/slugs/${data.moduleSlug}`, { data });

export const getFieldModulesByModuleId = async (data: {
  moduleId: string;
  status?: "buildBaseline" | "reviewBaseline" | "reviewedBaseline" | "predictPayout" | "reviewCurrentYear";
  itemsPerPage?: number;
  page?: number;
  signal: AbortSignal;
}): Promise<{ fieldModules: Omit<FullFieldModule, "wasShifted" | "verifications">[] }> => axios.get(`${endpoint}/${data.moduleId}/field-modules`, { data });

export const getFieldsWithModule = async (data: { moduleId: string; signal: AbortSignal }) => axios.get(`${endpoint}/${data.moduleId}/fields`, { data });

export const confirmBauData = async (data: { moduleId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/${data.moduleId}/field-modules/bau-entries/confirm`, {
    data,
  });

export const getBauFootprintPrediction = async (data: { moduleId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/${data.moduleId}/field-modules/bau-entries/predict-footprint`, {
    data,
  });

export const getYearlyFootprintPrediction = async (data: { moduleId: string; signal: AbortSignal }) =>
  axios.post(`${endpoint}/${data.moduleId}/field-modules/yearly-entries/predict-footprint`, {
    data,
  });

export const getUserModuleFieldStatuses = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/field-modules/statuses`, { data });

// farm field modules

export const addFieldModules = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/field-modules`, { data });

export const disableFieldModule = async (data: { id: string; signal: AbortSignal }) => axios.delete(`${endpoint}/field-modules/${data.id}`, { data });

export const getFieldModules = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/field-modules`, { data });

export const getFieldModule = async (data: { id: string; signal: AbortSignal }): Promise<FullFieldModule> =>
  axios.get(`${endpoint}/field-modules/${data.id}`, { data });

// fertilizers

export const getFertilizers = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/fertilizers`, { data });

export const addOrUpdateFertilizerBauData = async (data: AddOrUpdateBauEntryRequest) => axios.post(`${endpoint}/fertilizers/bau-entries`, { data });

export const addOrUpdateFertilizerYearlyData = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/fertilizers/yearly-entries`, { data });

// pesticides

export const addOrUpdatePesticidesBauData = async (data: AddOrUpdateBauEntryRequest) => axios.post(`${endpoint}/pesticides/bau-entries`, { data });

export const addOrUpdatePesticidesYearlyData = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/pesticides/yearly-entries`, { data });

// crop rotations

export const getCrops = async ({
  enabled,
}: {
  enabled?: boolean;
}): Promise<{ crops: Array<WithInjectedName<PracticeCrop>>; topCrops: Array<WithInjectedName<PracticeCrop>> }> =>
  axios.get(`${endpoint}/crops/?enabled=${enabled}`);

export const addOrUpdateCropsBauData = async (data: AddOrUpdateBauEntryRequest) => axios.post(`${endpoint}/crops/bau-entries`, { data });

export const addOrUpdateCropsYearlyData = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/crops/yearly-entries`, { data });

export const getFieldModuleCropsByYear = async (data: {
  selectedFieldModuleIds: string[];
  year: number;
  signal: AbortSignal;
}): Promise<FarmCropsByYearResponse> => axios.put(`${endpoint}/crops/field-modules/yearly-entries`, { data });

export const addFarmYearlyYieldLoss = async (data: {
  yieldLoss: boolean | null;
  signal: AbortSignal;
  fieldModuleIds: string[];
}): Promise<{ success: boolean }> => axios.put(`${endpoint}/crops/field-modules/yearly-entries/yield-loss`, { data });

export const getCropRecommendations = async (data: {
  cropId: string;
  fieldId: string;
  signal: AbortSignal;
}): Promise<{ sideCropId: string; payout: { min: number; max: number } }[]> =>
  axios.get(`${endpoint}/crops/${data.cropId}/recommendations`, { data: { fieldId: data.fieldId, recommendationType: "BOTH,AFTER" } });

export const getResiduePayoutPotential = async (
  data: GetResiduePayoutPotentialRequest
): Promise<{
  residuePayoutMin: number;
  residuePayoutAvg: number;
  residuePayoutMax: number;
}> => axios.get(`${endpoint}/crops/${data.cropId}/residue-payout-potential?hectares=${data.hectares}&regionId=${data.regionId}&fieldId=${data.fieldId}`);

export const getRedzoneCoverCropPayoutPotential = async (data: {
  cropId: string;
  hectares: number;
  regionId: string;
  residue: string | boolean;
  nonNeutralCropCount: number;
  signal: AbortSignal;
}) => axios.get(`${endpoint}/crops/${data.cropId}/redzone-cover-crop-payout-potential`, { data });

// tillage and seeding

export const getTillageSeedings = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/tillage-seedings`, { data });

export const addOrUpdateTillageSeedingBauData = async (data: AddOrUpdateBauEntryRequest) => axios.post(`${endpoint}/tillage-seedings/bau-entries`, { data });

export const addOrUpdateTillageSeedingYearlyData = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/tillage-seedings/yearly-entries`, { data });

// deadline

export const getCurrentYearlyDataEntryAddedStatus = async (data: { year: number; signal: AbortSignal }): Promise<YearlyDataEntryAddedStatus> =>
  axios.get(`${endpoint}/field-modules/statuses/yearly-entries/entry-added/${data.year}`, { signal: data.signal });

export const getSequestrationValueByCropAndZipCode = async (data: { cropId: string; country: string; zipCode: string; signal: AbortSignal }) =>
  axios.get(`${endpoint}/crops/${data.cropId}/regional-sequestration-values?country=${data.country}&zipCode=${data.zipCode}`, { signal: data.signal });
